import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

// One row from get_marked_leads (B3). The RPC takes the UI-selected org as
// p_organization_id (authorized server-side: super_admin OR active member — a
// cross-org viewer like Mako/Nathaniel reads the switched org, a setter reads only
// their own). target_setter: null = own-queue, "__ALL__" = Everyone (bounded), else
// a peer's ghl_user_id.
export interface MarkedLead {
  id: string; // ghl_conversations UUID (for ConversationViewModal + dismiss)
  ghl_conversation_id: string; // GHL text id (the tag join key)
  contact_name: string | null;
  lead_instagram: string | null;
  dmread_verdict: "QUALIFIABLE" | "BORDERLINE" | "REAL_DQ";
  dmread_money_signal: string | null;
  dmread_geo: string | null;
  dmread_life_stage: string | null;
  dmread_confidence: string | null;
  dmread_one_liner: string | null;
  dmread_signals: Record<string, unknown> | null;
  dmread_tagged_at: string | null;
  last_lead_msg_at: string | null;
  last_setter_msg_at: string | null;
  funnel_bucket: string | null;
  needs_response: boolean | null;
  is_own: boolean; // D5 write boundary — gates Reply in the UI
  in_window: boolean;
  hours_left: number | null;
  is_waiting: boolean;
  verdict_ord: number;
}

export const MARKED_ALL = "__ALL__";

interface UseMarkedLeadsResult {
  rows: MarkedLead[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * targetSetter: null -> own-queue (caller's assigned leads);
 * MARKED_ALL -> Everyone (org-wide, bounded, actionable only);
 * a ghl_user_id -> that peer's queue (validated in-org server-side).
 * organizationId is the UI-selected org; it gates the fetch, is a re-fetch
 * dependency, AND is passed to the RPC as p_organization_id (server authorizes it
 * super_admin-or-member, so a cross-org viewer sees the switched org's leads).
 */
export function useMarkedLeads(
  organizationId: string | null,
  targetSetter: string | null
): UseMarkedLeadsResult {
  const [rows, setRows] = useState<MarkedLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    if (!supabase || !organizationId) {
      setRows([]);
      return;
    }
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const controller = abortRef.current;
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase
        .rpc("get_marked_leads", {
          target_setter: targetSetter,
          p_organization_id: organizationId,
        })
        .abortSignal(controller.signal);
      if (controller.signal.aborted) return;
      if (rpcError) {
        setError(rpcError.message);
        setRows([]);
      } else {
        setRows((data as MarkedLead[]) ?? []);
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        setError(e instanceof Error ? e.message : "Failed to load marked leads");
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [organizationId, targetSetter]);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  return { rows, loading, error, refresh: fetchData };
}
